import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import { InfoIcon, CheckCircle, AlertCircle } from 'lucide-react';
import { Progress } from '../../components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { pmt2Service } from '../../services/pmt2Service';
import { PMT2SpreadsheetConfig, PMT2SyncStatus } from '../../types/pmt2Types';
import { PMT3SpreadsheetConfig } from '../../types/pmt3Types';
import { useToast } from '../../components/ui/use-toast';
import { Trash2, RefreshCw, ExternalLink, Edit, Save, X } from 'lucide-react';

/**
 * Admin section for managing PMT3 spreadsheet configurations
 */
const PMT2SpreadsheetConfigSection: React.FC = () => {
  const { toast } = useToast();
  const [spreadsheetId, setSpreadsheetId] = useState<string>('');
  const [farmingResultsSpreadsheetId, setFarmingResultsSpreadsheetId] = useState<string>('');
  const [companyName, setCompanyName] = useState<string>('');
  const [configs, setConfigs] = useState<PMT3SpreadsheetConfig[]>([]);
  const [syncStatus, setSyncStatus] = useState<PMT2SyncStatus | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [syncing, setSyncing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState<number>(0);
  const [isRetrying, setIsRetrying] = useState<boolean>(false);
  const [isChecking, setIsChecking] = useState<boolean>(false);
  const [checkResult, setCheckResult] = useState<{ accessible: boolean; message: string } | null>(null);
  const [isCheckingFarmingResults, setIsCheckingFarmingResults] = useState<boolean>(false);
  const [farmingResultsCheckResult, setFarmingResultsCheckResult] = useState<{ accessible: boolean; message: string } | null>(null);
  
  // State for confirmation dialog
  const [isConfirmOpen, setIsConfirmOpen] = useState<boolean>(false);
  const [configToRemove, setConfigToRemove] = useState<{ id: string, companyName: string } | null>(null);
  
  // State for editing
  const [editingConfigId, setEditingConfigId] = useState<string | null>(null);
  const [editClientBundleId, setEditClientBundleId] = useState<string>('');
  const [editFarmingResultsId, setEditFarmingResultsId] = useState<string>('');

  // State for migration
  const [migrationStatus, setMigrationStatus] = useState<{
    pmt2Count: number;
    pmt3Count: number;
    needsMigration: boolean;
  } | null>(null);
  const [isMigrating, setIsMigrating] = useState<boolean>(false);
  const [migrationResult, setMigrationResult] = useState<any>(null);

  // Load configurations on mount
  useEffect(() => {
    loadConfigs();
    loadSyncStatus();
    loadMigrationStatus();
  }, []);

  // Load spreadsheet configurations
  const loadConfigs = async () => {
    try {
      setLoading(true);
      // Import PMT3 service functions
      const { getPMT3SpreadsheetConfigs } = await import('../../services/pmt3ConfigService');
      const configsData = await getPMT3SpreadsheetConfigs();
      setConfigs(configsData);
    } catch (err) {
      console.error('Error loading configurations:', err);
      const errorMessage = 'Failed to load spreadsheet configurations';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
        duration: 5000
      });
      
      // If it's a permission error, offer to retry
      if (err instanceof Error && (err.message.includes('Access denied') || err.message.includes('403'))) {
        setError(`${errorMessage}\n\nThis appears to be a permissions issue. Google may need a few minutes to propagate permission changes.`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Load sync status
  const loadSyncStatus = async () => {
    try {
      const status = await pmt2Service.getSyncStatus();
      setSyncStatus(status as PMT2SyncStatus);
    } catch (error) {
      console.error('Error loading sync status:', error);
    }
  };

  // Load migration status
  const loadMigrationStatus = async () => {
    try {
      const { getMigrationStatus } = await import('../../services/pmt2ToPmt3Migration');
      const status = await getMigrationStatus();
      setMigrationStatus(status);
      console.log('Migration status:', status);
    } catch (error) {
      console.error('Error loading migration status:', error);
    }
  };

  // Handle migration
  const handleMigration = async () => {
    try {
      setIsMigrating(true);
      setMigrationResult(null);
      
      toast({
        title: 'Starting Migration',
        description: 'Migrating PMT2 configurations to PMT3 format. This may take a few minutes...',
        duration: 5000
      });

      const { migratePMT2ToPMT3 } = await import('../../services/pmt2ToPmt3Migration');
      const result = await migratePMT2ToPMT3();
      
      setMigrationResult(result);
      
      if (result.success) {
        toast({
          title: 'Migration Completed',
          description: `Successfully migrated ${result.migratedConfigs} companies. ${result.skippedConfigs} were already migrated.`,
          duration: 8000
        });
        
        // Reload configurations and migration status
        await Promise.all([loadConfigs(), loadMigrationStatus()]);
      } else {
        toast({
          title: 'Migration Failed',
          description: `Migration completed with ${result.failedConfigs} failures. Check console for details.`,
          variant: 'destructive',
          duration: 8000
        });
      }
      
    } catch (error) {
      console.error('Error during migration:', error);
      toast({
        title: 'Migration Error',
        description: 'An unexpected error occurred during migration.',
        variant: 'destructive',
        duration: 5000
      });
    } finally {
      setIsMigrating(false);
    }
  };

  // Check Client Bundle SKUs spreadsheet access
  const checkSpreadsheetAccess = async () => {
    if (!spreadsheetId.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Please enter a Client Bundle SKUs Spreadsheet ID to check',
        variant: 'destructive',
        duration: 5000
      });
      return;
    }
    
    setIsChecking(true);
    setCheckResult(null);
    setError(null);
    
    try {
      console.log(`Checking access for Client Bundle SKUs spreadsheet ID: ${spreadsheetId}`);
      const result = await pmt2Service.checkSpreadsheetAccess(spreadsheetId);
      
      if (result.accessible) {
        setCheckResult({
          accessible: true,
          message: `Client Bundle SKUs spreadsheet "${result.details?.spreadsheetTitle}" is accessible and contains the required sheets.`
        });
        toast({
          title: 'Success',
          description: 'Client Bundle SKUs spreadsheet is accessible with the correct permissions and sheet names.',
          duration: 5000
        });
      } else {
        setCheckResult({
          accessible: false,
          message: result.error || 'Unknown error occurred when checking Client Bundle SKUs spreadsheet access.'
        });
        toast({
          title: 'Access Check Failed',
          description: result.error || 'Unknown error occurred when checking Client Bundle SKUs spreadsheet access.',
          variant: 'destructive',
          duration: 5000
        });
      }
    } catch (error) {
      console.error('Error checking Client Bundle SKUs spreadsheet access:', error);
      setCheckResult({
        accessible: false,
        message: error instanceof Error ? error.message : 'Unknown error occurred when checking Client Bundle SKUs spreadsheet access.'
      });
    } finally {
      setIsChecking(false);
    }
  };

  // Check Farming Results spreadsheet access
  const checkFarmingResultsSpreadsheetAccess = async () => {
    if (!farmingResultsSpreadsheetId.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Please enter a Farming Results Spreadsheet ID to check',
        variant: 'destructive',
        duration: 5000
      });
      return;
    }
    
    setIsCheckingFarmingResults(true);
    setFarmingResultsCheckResult(null);
    setError(null);
    
    try {
      console.log(`Checking access for Farming Results spreadsheet ID: ${farmingResultsSpreadsheetId}`);
      // Import the function directly since we need it for the new validation
      const { checkFarmingResultsSpreadsheetAccess } = await import('../../services/pmt3ConfigService');
      const result = await checkFarmingResultsSpreadsheetAccess(farmingResultsSpreadsheetId);
      
      if (result.accessible) {
        setFarmingResultsCheckResult({
          accessible: true,
          message: `Farming Results spreadsheet "${result.details?.spreadsheetTitle}" is accessible and contains the required "Disposition Ledger" and "All CST Results" sheets.`
        });
        toast({
          title: 'Success',
          description: 'Farming Results spreadsheet is accessible with the correct permissions and sheet names.',
          duration: 5000
        });
      } else {
        setFarmingResultsCheckResult({
          accessible: false,
          message: result.error || 'Unknown error occurred when checking Farming Results spreadsheet access.'
        });
        toast({
          title: 'Access Check Failed',
          description: result.error || 'Unknown error occurred when checking Farming Results spreadsheet access.',
          variant: 'destructive',
          duration: 5000
        });
      }
    } catch (error) {
      console.error('Error checking Farming Results spreadsheet access:', error);
      setFarmingResultsCheckResult({
        accessible: false,
        message: error instanceof Error ? error.message : 'Unknown error occurred when checking Farming Results spreadsheet access.'
      });
    } finally {
      setIsCheckingFarmingResults(false);
    }
  };

  // Retry adding spreadsheet with a delay
  const retryAddSpreadsheet = async () => {
    if (retryCount >= 3) {
      setError("Maximum retry attempts reached (3/3). Please check the spreadsheet permissions and try again later.");
      setIsRetrying(false);
      setRetryCount(0);
      return;
    }

    // Increment retry count first
    const currentRetry = retryCount + 1;
    setRetryCount(currentRetry);
    setIsRetrying(true);
    setError(`Retrying... (Attempt ${currentRetry}/3). Waiting for Google's permission changes to propagate.`);
    
    // Wait for 10 seconds before retrying
    await new Promise(resolve => setTimeout(resolve, 10000));
    
    try {
      console.log(`Retry attempt ${currentRetry} for spreadsheet config - ID: ${spreadsheetId}, Company: ${companyName}`);
      await pmt2Service.addSpreadsheetConfig(spreadsheetId, companyName);
      
      toast({
        title: 'Success',
        description: `Added spreadsheet configuration for ${companyName} after retry`,
        duration: 5000
      });
      
      // Reset form
      setSpreadsheetId('');
      setCompanyName('');
      setIsRetrying(false);
      setRetryCount(0);
      
      // Reload configurations
      await loadConfigs();
    } catch (error) {
      console.error(`Retry ${currentRetry} failed:`, error);
      
      // If we haven't reached max retries, try again
      if (currentRetry < 3) {
        // Wait a bit before trying again
        setTimeout(() => {
          retryAddSpreadsheet();
        }, 1000);
      } else {
        setError(`All retry attempts failed (${currentRetry}/3). Please check the spreadsheet permissions and try again later.`);
        setIsRetrying(false);
      }
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setRetryCount(0); 
    setIsRetrying(false);
    
    if (!spreadsheetId.trim() || !companyName.trim()) {
      setError('Spreadsheet ID and Company Name are required');
      toast({
        title: 'Validation Error',
        description: 'Spreadsheet ID and Company Name are required',
        variant: 'destructive',
        duration: 5000
      });
      return;
    }
    
    try {
      setLoading(true);
      console.log(`Submitting spreadsheet config - Client Bundle SKUs ID: ${spreadsheetId}, Farming Results ID: ${farmingResultsSpreadsheetId || 'None'}, Company: ${companyName}`);
      
      // Import the updated function that supports dual spreadsheets
      const { addPMT3SpreadsheetConfig } = await import('../../services/pmt3ConfigService');
      await addPMT3SpreadsheetConfig(
        spreadsheetId,
        companyName,
        farmingResultsSpreadsheetId.trim() || undefined
      );
      
      toast({
        title: 'Success',
        description: `Added spreadsheet configuration for ${companyName}${farmingResultsSpreadsheetId ? ' with Farming Results spreadsheet' : ''}`,
        duration: 5000
      });
      
      // Reset form
      setSpreadsheetId('');
      setFarmingResultsSpreadsheetId('');
      setCompanyName('');
      setCheckResult(null);
      setFarmingResultsCheckResult(null);
      
      // Reload configurations
      await loadConfigs();
    } catch (error) {
      console.error('Error adding spreadsheet configuration:', error);
      
      // Extract error message for display
      let errorMessage = 'Failed to add spreadsheet configuration';
      
      let isPermissionError = false;
      if (error instanceof Error) {
        console.error('Error details:', error.message);
        errorMessage = error.message;
      } else {
        console.error('Unknown error type:', error);
      }
      
      // Set error for display in the UI
      setError(errorMessage);
      
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
        duration: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle configuration removal
  const handleRemoveConfig = (configId: string, companyName: string) => {
    // Open confirmation dialog
    setConfigToRemove({ id: configId, companyName });
    setIsConfirmOpen(true);
  };
  
  // Handle confirmation dialog confirm
  const handleConfirmRemove = async () => {
    if (!configToRemove) return;
    
    try {
      setLoading(true);
      await pmt2Service.removeSpreadsheetConfig(configToRemove.id);
      
      toast({
        title: 'Success',
        description: `Removed spreadsheet configuration for ${configToRemove.companyName}`,
        duration: 5000
      });
      
      // Reload configurations
      await loadConfigs();
    } catch (error) {
      console.error('Error removing configuration:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove spreadsheet configuration',
        variant: 'destructive',
        duration: 5000
      });
    } finally {
      setLoading(false);
    }
    
    // Reset state
    setConfigToRemove(null);
    setIsConfirmOpen(false);
  };

  // Handle edit configuration
  const handleEditConfig = (config: PMT3SpreadsheetConfig) => {
    setEditingConfigId(config.id || null);
    setEditClientBundleId(config.spreadsheetId);
    setEditFarmingResultsId(config.farmingResultsSpreadsheetId || '');
  };

  // Handle save edit
  const handleSaveEdit = async () => {
    if (!editingConfigId) return;
    
    try {
      setLoading(true);
      // Find the current config to get the company name
      const currentConfig = configs.find(config => config.id === editingConfigId);
      if (!currentConfig) throw new Error('Configuration not found');

      // Import the updated function that supports dual spreadsheets
      const { updatePMT3SpreadsheetConfig } = await import('../../services/pmt3ConfigService');
      await updatePMT3SpreadsheetConfig(editingConfigId, {
        spreadsheetId: editClientBundleId,
        farmingResultsSpreadsheetId: editFarmingResultsId.trim() || undefined
      });
      
      toast({
        title: "Configuration Updated",
        description: `The spreadsheet configuration for ${currentConfig.companyName} has been successfully updated.`
      });
      
      // Reload configurations
      await loadConfigs();
    } catch (error) {
      console.error('Error updating spreadsheet configuration:', error);
      toast({
        title: "Error",
        description: "Failed to update spreadsheet configuration. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
      handleCancelEdit();
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingConfigId(null);
    setEditClientBundleId('');
    setEditFarmingResultsId('');
  };

  // Handle manual sync
  const handleManualSync = async () => {
    try {
      // Show a friendly toast notification about the sync process
      toast({
        title: '🚀 Sync in Progress',
        description: '📊 Importing company data may take 15+ minutes. Grab a coffee while our digital elves do their magic! ☕',
        duration: 5000
      });

      setSyncing(true);

      try {
        // Attempt to trigger the sync
        await pmt2Service.triggerManualSync();

        // If it completes quickly (which is rare), show a success message
        toast({
          title: '✅ Sync Initiated',
          description: 'Manual sync triggered successfully. Data will update as the process completes.',
          duration: 5000
        });
      } catch (error) {
        // If we get a timeout error, that's actually expected behavior
        // The sync continues in the background despite the timeout
        console.log('Sync timeout occurred, but this is expected behavior. Sync continues in the background.');

        // No error toast needed here since we already showed the "in progress" toast
      }

      // Reload sync status after a delay
      setTimeout(async () => {
        await loadSyncStatus();
        setSyncing(false);
      }, 2000);
    } catch (error) {
      // Only log the error, but don't show an error toast since the sync likely continues anyway
      console.error('Unexpected error in sync handler:', error);
      setSyncing(false);
    }
  };

  // Format timestamp - handles both Firestore Timestamps and Date objects
  const formatTimestamp = (timestamp: any) => {
    if (!timestamp) return 'Never';
    
    try {
      // Handle Firestore Timestamp objects
      if (timestamp.toMillis && typeof timestamp.toMillis === 'function') {
        return new Date(timestamp.toMillis()).toLocaleString();
      }
      // Handle regular Date objects
      if (timestamp instanceof Date) {
        return timestamp.toLocaleString();
      }
      // Handle date strings or numbers
      return new Date(timestamp).toLocaleString();
    } catch (e) {
      console.warn('Error formatting timestamp:', timestamp, e);
      return 'Invalid date';
    }
  };

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>PMT3 Spreadsheet Configurations</CardTitle>
        <CardDescription>
          Manage Google Sheets spreadsheets for Amazon seller data
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Add New Configuration */}
        <form onSubmit={handleSubmit} className="space-y-4 mb-6">
          <h3 className="text-lg font-semibold">Add New Spreadsheet</h3>
          
          {error && (
            <Alert variant={isRetrying ? "default" : "destructive"} className="mb-4">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
              
              {error.includes('Access denied') && !isRetrying && (
                <div className="mt-4 flex flex-col gap-2">
                  <Button 
                    onClick={retryAddSpreadsheet} 
                    variant="outline" 
                    size="sm"
                    disabled={isRetrying}
                  >
                    Retry (Wait for permissions to propagate)
                  </Button>
                  <div className="text-sm text-muted-foreground mt-2">
                    <p>If retries fail, please check:</p>
                    <ol className="list-decimal pl-5 mt-1 space-y-1">
                      <li>The spreadsheet is shared with <strong>jls-apps@jls-apps.iam.gserviceaccount.com</strong></li>
                      <li>The service account has <strong>Editor</strong> access (not just Viewer)</li>
                      <li>The sheet is named exactly <strong>"Client Bundle SKUs"</strong></li>
                      <li>Try creating a new test spreadsheet with a simple structure</li>
                    </ol>
                  </div>
                </div>
              )}
            </Alert>
          )}

          <Alert className="mb-4">
            <InfoIcon className="h-4 w-4" />
            <AlertTitle>Important: Spreadsheet Sharing</AlertTitle>
            <AlertDescription>
              <p>Before adding spreadsheets, please ensure:</p>
              <div className="mt-2 space-y-3">
                <div>
                  <p className="font-semibold">Client Bundle SKUs Spreadsheet (Required):</p>
                  <ol className="list-decimal pl-5 mt-1 space-y-1">
                    <li>Contains sheets named exactly <strong>"Indirects"</strong> and <strong>"Bundle Ledger"</strong></li>
                    <li>Is shared with <strong>jls-apps@jls-apps.iam.gserviceaccount.com</strong></li>
                    <li>Service account has <strong>Editor</strong> access (not just Viewer)</li>
                  </ol>
                </div>
                <div>
                  <p className="font-semibold">Farming Results Spreadsheet (Optional):</p>
                  <ol className="list-decimal pl-5 mt-1 space-y-1">
                    <li>Contains sheets named exactly <strong>"Disposition Ledger"</strong> and <strong>"All CST Results"</strong></li>
                    <li>Is shared with <strong>jls-apps@jls-apps.iam.gserviceaccount.com</strong></li>
                    <li>Service account has <strong>Editor</strong> access (not just Viewer)</li>
                  </ol>
                </div>
                <p className="text-sm text-muted-foreground">Use the "Check Access" buttons below to verify permissions before adding.</p>
              </div>
            </AlertDescription>
          </Alert>
          
          <div className="grid grid-cols-1 gap-6">
            {/* Client Bundle SKUs Spreadsheet */}
            <div className="space-y-4">
              <h4 className="text-md font-medium">Client Bundle SKUs Spreadsheet (Required)</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="spreadsheetId">Client Bundle SKUs Spreadsheet ID</Label>
                  <Input
                    id="spreadsheetId"
                    value={spreadsheetId}
                    onChange={(e) => setSpreadsheetId(e.target.value)}
                    placeholder="Enter Client Bundle SKUs spreadsheet ID"
                    disabled={loading}
                  />
                  <p className="text-sm text-muted-foreground">
                    The ID from the Client Bundle SKUs spreadsheet URL
                  </p>
                  <div className="mt-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={checkSpreadsheetAccess}
                      disabled={isChecking || !spreadsheetId.trim()}
                      className="flex items-center gap-2"
                    >
                      {isChecking ? 'Checking...' : 'Check Client Bundle SKUs Access'}
                    </Button>
                  </div>
                  
                  {checkResult && (
                    <div className={`mt-2 p-2 rounded-md text-sm flex items-start gap-2 ${
                      checkResult.accessible
                        ? 'bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                        : 'bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                    }`}>
                      {checkResult.accessible
                        ? <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        : <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />}
                      <span>{checkResult.message}</span>
                    </div>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="companyName">Company Name</Label>
                  <Input
                    id="companyName"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="Enter company name"
                    disabled={loading}
                  />
                  <p className="text-sm text-muted-foreground">
                    A unique name to identify this seller
                  </p>
                </div>
              </div>
            </div>

            {/* Farming Results Spreadsheet */}
            <div className="space-y-4">
              <h4 className="text-md font-medium">Farming Results Spreadsheet (Optional)</h4>
              <div className="space-y-2">
                <Label htmlFor="farmingResultsSpreadsheetId">Farming Results Spreadsheet ID</Label>
                <Input
                  id="farmingResultsSpreadsheetId"
                  value={farmingResultsSpreadsheetId}
                  onChange={(e) => setFarmingResultsSpreadsheetId(e.target.value)}
                  placeholder="Enter Farming Results spreadsheet ID (optional)"
                  disabled={loading}
                />
                <p className="text-sm text-muted-foreground">
                  The ID from the Farming Results spreadsheet URL. Leave empty if not available yet.
                </p>
                <div className="mt-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={checkFarmingResultsSpreadsheetAccess}
                    disabled={isCheckingFarmingResults || !farmingResultsSpreadsheetId.trim()}
                    className="flex items-center gap-2"
                  >
                    {isCheckingFarmingResults ? 'Checking...' : 'Check Farming Results Access'}
                  </Button>
                </div>
                
                {farmingResultsCheckResult && (
                  <div className={`mt-2 p-2 rounded-md text-sm flex items-start gap-2 ${
                    farmingResultsCheckResult.accessible
                      ? 'bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                      : 'bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                  }`}>
                    {farmingResultsCheckResult.accessible
                      ? <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      : <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />}
                    <span>{farmingResultsCheckResult.message}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <Button
            type="submit"
            disabled={
              loading ||
              (checkResult !== null && !checkResult.accessible) ||
              (farmingResultsSpreadsheetId.trim() !== '' && farmingResultsCheckResult !== null && !farmingResultsCheckResult.accessible)
            }
            className="bg-orange-500 hover:bg-orange-600"
          >
            {loading ? 'Adding...' : 'Add Spreadsheets'}
          </Button>
          {((checkResult !== null && !checkResult.accessible) ||
            (farmingResultsSpreadsheetId.trim() !== '' && farmingResultsCheckResult !== null && !farmingResultsCheckResult.accessible)) && (
            <p className="text-sm text-red-500 mt-2">
              Please resolve all access issues before adding spreadsheets.
            </p>
          )}
        </form>
        
        <Separator className="my-6" />
        
        {/* Migration Section - only show if migration is actually needed */}
        {migrationStatus && migrationStatus.needsMigration && migrationStatus.pmt2Count > migrationStatus.pmt3Count && (
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Data Migration</h3>
            </div>
            
            <Alert className="mb-4">
              <InfoIcon className="h-4 w-4" />
              <AlertTitle>PMT2 to PMT3 Migration Required</AlertTitle>
              <AlertDescription>
                <div className="mt-2 space-y-2">
                  <p>Found {migrationStatus.pmt2Count} PMT2 configurations and {migrationStatus.pmt3Count} PMT3 configurations.</p>
                  <p>Click "Migrate Now" to convert your existing PMT2 configurations to the new PMT3 format with dual spreadsheet support.</p>
                  <div className="flex items-center gap-2 mt-4">
                    <Button
                      onClick={handleMigration}
                      disabled={isMigrating}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {isMigrating ? 'Migrating...' : 'Migrate Now'}
                    </Button>
                    {isMigrating && <div className="text-sm text-muted-foreground">This may take a few minutes...</div>}
                  </div>
                </div>
              </AlertDescription>
            </Alert>
            
            {migrationResult && (
              <Alert variant={migrationResult.success ? "default" : "destructive"} className="mb-4">
                <AlertTitle>Migration {migrationResult.success ? 'Completed' : 'Failed'}</AlertTitle>
                <AlertDescription>
                  <div className="space-y-1">
                    <p>Total PMT2 Configurations: {migrationResult.totalPMT2Configs}</p>
                    <p>Successfully Migrated: {migrationResult.migratedConfigs}</p>
                    <p>Skipped (Already Migrated): {migrationResult.skippedConfigs}</p>
                    <p>Failed: {migrationResult.failedConfigs}</p>
                    {migrationResult.errors.length > 0 && (
                      <div className="mt-2">
                        <p className="font-medium">Errors:</p>
                        <ul className="list-disc pl-5">
                          {migrationResult.errors.map((error: any, index: number) => (
                            <li key={index} className="text-sm">{error.companyName}: {error.error}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}
        
        <Separator className="my-6" />
        
        {/* Sync Status */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Sync Status</h3>
            <Button 
              onClick={handleManualSync} 
              disabled={syncing}
              variant="outline"
              className="flex items-center gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync Now'}
            </Button>
          </div>
          
          {syncStatus ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-muted p-3 rounded-md">
                  <div className="text-sm text-muted-foreground">Last Sync</div>
                  <div className="text-base font-medium">
                    {syncStatus.lastSync ? formatTimestamp(syncStatus.lastSync) : 'Never'}
                  </div>
                </div>
                
                <div className="bg-muted p-3 rounded-md">
                  <div className="text-sm text-muted-foreground">Status</div>
                  <div className={`text-base font-medium ${
                    syncStatus.inProgress 
                      ? 'text-blue-500' 
                      : syncStatus.lastSyncSuccess 
                        ? 'text-green-500' 
                        : 'text-red-500'
                  }`}>
                    {syncStatus.inProgress 
                      ? 'In Progress' 
                      : syncStatus.lastSyncSuccess 
                        ? 'Success' 
                        : 'Failed'}
                  </div>
                </div>
                
                <div className="bg-muted p-3 rounded-md">
                  <div className="text-sm text-muted-foreground">Products</div>
                  <div className="text-base font-medium">
                    {syncStatus.processedProducts || 0}
                  </div>
                </div>
                
                <div className="bg-muted p-3 rounded-md">
                  <div className="text-sm text-muted-foreground">Changes</div>
                  <div className="text-base font-medium">
                    {syncStatus.totalChanges || 0}
                  </div>
                </div>
              </div>
              
              {syncStatus.error && (
                <Alert variant="destructive" className="mt-4">
                  <AlertTitle>Sync Error</AlertTitle>
                  <AlertDescription>{syncStatus.error}</AlertDescription>
                </Alert>
              )}
            </div>
          ) : (
            <p className="text-muted-foreground">No sync information available</p>
          )}
        </div>
        
        <Separator className="my-6" />
        
        {/* Configurations List */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Configured Spreadsheets</h3>
          
          {configs.length === 0 ? (
            <p className="text-muted-foreground">No spreadsheets configured yet</p>
          ) : (
            <div className="border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company</TableHead>
                    <TableHead>Client Bundle SKUs</TableHead>
                    <TableHead>Farming Results</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {configs.map((config) => (
                    <TableRow key={config.id}>
                      <TableCell className="font-medium">{config.companyName}</TableCell>
                      <TableCell>
                        {editingConfigId === config.id ? (
                          <div className="flex items-center gap-2">
                            <Input
                              value={editClientBundleId}
                              onChange={(e) => setEditClientBundleId(e.target.value)}
                              className="text-xs"
                              placeholder="Client Bundle SKUs ID"
                            />
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className="text-sm truncate max-w-[150px]" title={config.spreadsheetId}>
                              {config.spreadsheetId}
                            </span>
                            <a
                              href={`https://docs.google.com/spreadsheets/d/${config.spreadsheetId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-500 hover:text-blue-700"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {editingConfigId === config.id ? (
                          <div className="flex items-center gap-2">
                            <Input
                              value={editFarmingResultsId}
                              onChange={(e) => setEditFarmingResultsId(e.target.value)}
                              className="text-xs"
                              placeholder="Farming Results ID (optional)"
                            />
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            {config.farmingResultsSpreadsheetId ? (
                              <>
                                <span className="text-sm truncate max-w-[150px]" title={config.farmingResultsSpreadsheetId}>
                                  {config.farmingResultsSpreadsheetId}
                                </span>
                                <a
                                  href={`https://docs.google.com/spreadsheets/d/${config.farmingResultsSpreadsheetId}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-500 hover:text-blue-700"
                                >
                                  <ExternalLink className="h-4 w-4" />
                                </a>
                              </>
                            ) : (
                              <span className="text-sm text-muted-foreground">Not set</span>
                            )}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                          Active
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center gap-1 justify-end">
                          {editingConfigId === config.id ? (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleSaveEdit}
                                disabled={loading}
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              >
                                <Save className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleCancelEdit}
                                className="text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditConfig(config)}
                              className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveConfig(config.id || '', config.companyName)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            disabled={!config.id}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </CardContent>

      {/* Confirmation Dialog */}
      <AlertDialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the spreadsheet configuration for {configToRemove?.companyName}.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfigToRemove(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmRemove}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default PMT2SpreadsheetConfigSection;